// src/products/analysis/services/treasuryService.js
// FRED (Federal Reserve Economic Data) API
// Updated to use backend proxy endpoints (API keys stored securely in Supabase)
import { getAuthHeaders } from '../../../services/authService';

const API_BASE_URL = '/api/analysis';

/**
 * Helper to check if response is HTML (backend not available)
 */
const checkResponseType = (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    throw new Error('Backend API not available. Please ensure the backend server is running or deploy to Vercel.');
  }
};

/**
 * Treasury series IDs for different maturities
 */
export const TREASURY_SERIES = {
  '1MO': { id: 'DGS1MO', name: '1-Month Treasury', maturity: '1 Month' },
  '3MO': { id: 'DGS3MO', name: '3-Month Treasury', maturity: '3 Months' },
  '6MO': { id: 'DGS6MO', name: '6-Month Treasury', maturity: '6 Months' },
  '1Y': { id: 'DGS1', name: '1-Year Treasury', maturity: '1 Year' },
  '2Y': { id: 'DGS2', name: '2-Year Treasury', maturity: '2 Years' },
  '3Y': { id: 'DGS3', name: '3-Year Treasury', maturity: '3 Years' },
  '5Y': { id: 'DGS5', name: '5-Year Treasury', maturity: '5 Years' },
  '7Y': { id: 'DGS7', name: '7-Year Treasury', maturity: '7 Years' },
  '10Y': { id: 'DGS10', name: '10-Year Treasury', maturity: '10 Years' },
  '20Y': { id: 'DGS20', name: '20-Year Treasury', maturity: '20 Years' },
  '30Y': { id: 'DGS30', name: '30-Year Treasury', maturity: '30 Years' }
};

/**
 * Get Treasury yield data from FRED
 * @param {string} seriesId - FRED series ID (e.g., 'DGS10')
 * @param {number} limit - Number of recent observations to return
 * @returns {Promise<Object>} - Treasury data with yield and dates
 */
export const getTreasuryYield = async (seriesId, limit = 30) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/fred/observations?series_id=${encodeURIComponent(seriesId)}&limit=${limit}&sort_order=desc&observation_start=${getDateDaysAgo(365)}`,
      { headers }
    );

    checkResponseType(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.observations || !Array.isArray(data.observations)) {
      throw new Error('Invalid response format from FRED API');
    }

    // Filter out observations with '.' value (no data)
    const validObservations = data.observations
      .filter(obs => obs.value !== '.' && obs.value !== null && obs.value !== undefined)
      .map(obs => ({
        date: obs.date,
        timestamp: new Date(obs.date).getTime(),
        value: parseFloat(obs.value),
        yield: parseFloat(obs.value) // Yield percentage
      }))
      .sort((a, b) => a.timestamp - b.timestamp); // Sort oldest to newest

    if (validObservations.length === 0) {
      throw new Error(`No valid data available for ${seriesId}`);
    }

    const latest = validObservations[validObservations.length - 1];
    const previous = validObservations.length > 1 ? validObservations[validObservations.length - 2] : latest;

    return {
      seriesId,
      currentYield: latest.yield,
      previousYield: previous.yield,
      yieldChange: latest.yield - previous.yield,
      yieldChangePercent: previous.yield !== 0 ? ((latest.yield - previous.yield) / previous.yield) * 100 : 0,
      date: latest.date,
      historicalData: validObservations
    };
  } catch (error) {
    console.error(`FRED Treasury error for ${seriesId}:`, error);
    throw error;
  }
};

/**
 * Get all major Treasury yields
 * @returns {Promise<Array>} - Array of Treasury data objects
 */
export const getAllTreasuryYields = async () => {
  const series = Object.values(TREASURY_SERIES);

  // Fetch all Treasury yields in parallel (FRED allows 120 requests/minute)
  const promises = series.map(async (treasury) => {
    try {
      const data = await getTreasuryYield(treasury.id, 30);
      return {
        ...treasury,
        currentYield: data.currentYield,
        previousYield: data.previousYield,
        yieldChange: data.yieldChange,
        yieldChangePercent: data.yieldChangePercent,
        date: data.date,
        historicalData: data.historicalData
      };
    } catch (error) {
      console.warn(`Failed to fetch ${treasury.name}:`, error.message);
      return {
        ...treasury,
        currentYield: null,
        previousYield: null,
        yieldChange: null,
        yieldChangePercent: null,
        date: null,
        error: error.message
      };
    }
  });

  const resultsData = await Promise.all(promises);
  
  return resultsData;
};

/**
 * Get historical Treasury data for charts
 * @param {string} seriesId - FRED series ID
 * @param {number} days - Number of days of historical data
 * @returns {Promise<Array>} - Historical data points
 */
export const getTreasuryHistory = async (seriesId, days = 90) => {
  try {
    const startDate = getDateDaysAgo(days);
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/fred/observations?series_id=${encodeURIComponent(seriesId)}&observation_start=${startDate}&sort_order=asc`,
      { headers }
    );

    checkResponseType(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.observations || !Array.isArray(data.observations)) {
      return [];
    }

    // Filter and format historical data
    return data.observations
      .filter(obs => obs.value !== '.' && obs.value !== null && obs.value !== undefined)
      .map(obs => ({
        date: new Date(obs.date).toISOString(),
        timestamp: new Date(obs.date).getTime(),
        value: parseFloat(obs.value),
        yield: parseFloat(obs.value),
        close: parseFloat(obs.value) // For compatibility with chart components
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  } catch (error) {
    console.error(`FRED history error for ${seriesId}:`, error);
    throw error;
  }
};

/**
 * Helper to get date string N days ago (YYYY-MM-DD format)
 */
const getDateDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

/**
 * Get Treasury yield curve (all maturities at once)
 * @returns {Promise<Array>} - Array of yields by maturity
 */
export const getYieldCurve = async () => {
  try {
    const yields = await getAllTreasuryYields();
    
    // Filter out failed requests
    return yields
      .filter(t => t.currentYield !== null && !t.error)
      .sort((a, b) => {
        // Sort by maturity order
        const order = ['1MO', '3MO', '6MO', '1Y', '2Y', '3Y', '5Y', '7Y', '10Y', '20Y', '30Y'];
        return order.indexOf(a.id) - order.indexOf(b.id);
      });
  } catch (error) {
    console.error('Error fetching yield curve:', error);
    throw error;
  }
};

